import { useMemo } from 'react';
import classNames from 'classnames';

import { useViewportType } from '../utils/useViewportType';
import { Image } from '../Image/Image';
import { getAssetMainImage } from '../../shared/helpers/assetHelpers';
import { formatIsoDateTime } from '../../utils/datetime';
import { getAssetPublishedDate } from '../../helpers/getAssetProps';

import type { ImageSources } from '../../helpers/images';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { FC } from 'react';

import * as styles from './AssetContainer.module.scss';

interface AssetContainerProps {
  className?: string;
  disableAssetBackground?: boolean;
  asset: SvpAssetAdditional;
}

const AssetContainer: FC<AssetContainerProps> = ({
  children,
  className,
  disableAssetBackground = false,
  asset,
}) => {
  const { isDesktop } = useViewportType();

  const imageSources = useMemo(
    (): ImageSources => ({
      src: `${getAssetMainImage(asset)}?t[]=${window.POSTER_TRANSFORM}`,
      aspectRatio: 'horizontal',
    }),
    [asset]
  );

  const publishedDate = useMemo(() => {
    const date = getAssetPublishedDate(asset);
    return date ? formatIsoDateTime(date) : undefined;
  }, [asset]);

  return (
    <main className={classNames(styles.container, className)}>
      {isDesktop && !disableAssetBackground && (
        <Image
          className={styles.image}
          imageSources={imageSources}
          lazy={false}
          isRounded={false}
        />
      )}
      {publishedDate && (
        <time
          className={styles.microformat}
          itemProp="datePublished"
          dateTime={publishedDate}
        >
          {publishedDate}
        </time>
      )}
      {children}
    </main>
  );
};

export { AssetContainer };
